/**
 * LUNE — Moteur de conversation (state machine) + abstraction conversationProvider.
 *
 * Le moteur ne touche jamais au DOM : il reçoit du texte/des clics et renvoie une
 * liste de "messages" que l'UI (lune-ui.js) affiche. Cela permet de remplacer plus
 * tard le provider local par un vrai LLM sans reconstruire l'interface — il suffira
 * d'ajouter un Lune.Providers.llmProvider avec la même signature respond(session, input)
 * et de changer Lune.Providers.active.
 */
window.Lune = window.Lune || {};

Lune.Providers = (function () {
  'use strict';

  /**
   * localProvider : toute la logique v1, sans API, basée sur :
   * - la base de connaissances (snippets/lune-knowledge.liquid)
   * - le moteur NLU (lune-nlu.js)
   * - le moteur de recommandation (lune-recommend.js)
   * Interface commune pour un futur provider LLM : respond(session, input) -> { messages }
   */
  var localProvider = {
    respond: function (session, input) {
      var NLU = Lune.NLU;
      var Analytics = Lune.Analytics;

      if (input.type === 'button') {
        return handleButton(session, input.action, input.payload);
      }

      // Texte libre.
      var text = input.text || '';
      Analytics.track(Analytics.EVENTS.QUESTION, { text_length: text.length });

      var dims = NLU.extractDimensions(text);
      if (dims) {
        return dimensionsAnswer(session, dims);
      }

      var intent = NLU.matchIntent(text, session.knowledge);
      if (intent) {
        return faqAnswer(intent);
      }

      var criteria = NLU.extractCriteria(text);
      if (criteria.room.length || criteria.style.length || criteria.colors.length) {
        session.answers.room = criteria.room.length ? criteria.room : session.answers.room;
        session.answers.style = criteria.style.length ? criteria.style : session.answers.style;
        session.answers.colors = criteria.colors.length ? criteria.colors : session.answers.colors;
        return showResults(session);
      }

      Analytics.track(Analytics.EVENTS.NO_ANSWER, { text_length: text.length });
      return {
        messages: [
          { type: 'bot_text', text: 'Je n\'ai pas toute l\'information nécessaire pour répondre précisément à cela. Voici comment je peux vous aider :' },
          greetingButtons()
        ]
      };
    }
  };

  function greetingButtons() {
    return {
      type: 'bot_buttons',
      buttons: [
        { label: 'Trouver mon décor', action: 'find_decor' },
        { label: 'Choisir ma finition', action: 'choose_finish' },
        { label: 'J\'ai une question', action: 'ask_question' }
      ]
    };
  }

  function faqAnswer(intent) {
    var text = intent.answer || Lune.currentKnowledgeFallback || 'Cette information n\'est pas encore disponible.';
    var messages = [{ type: 'bot_text', text: text }];
    if (intent.link) {
      messages.push({ type: 'bot_link', label: intent.link.label, url: intent.link.url });
    }
    return { messages: messages };
  }

  function frNumber(n) {
    return String(n).replace('.', ',');
  }

  function dimensionsAnswer(session, dims) {
    var text = 'Votre mur représente environ ' + frNumber(dims.areaM2) + ' m² (' + frNumber(dims.widthM) + ' m × ' + frNumber(dims.heightM) + ' m).' +
      ' Pour commander votre décor aux bonnes dimensions, utilisez notre configurateur qui adaptera automatiquement le visuel et le prix à votre mur.';
    var messages = [{ type: 'bot_text', text: text }];
    var target = session.pageContext && session.pageContext.product ? session.pageContext.product.url : null;
    messages.push({
      type: 'bot_buttons',
      buttons: target
        ? [{ label: 'Configurer mon mur', action: 'pwp_link', payload: { url: target } }]
        : [{ label: 'Trouver mon décor', action: 'find_decor' }]
    });
    return { messages: messages };
  }

  var QUESTIONS = {
    room: {
      prompt: 'Pour quelle pièce recherchez-vous un décor ?',
      options: ['Salon', 'Chambre', 'Suite / Hôtel', 'Bureau', 'Restaurant', 'Entrée / Couloir', 'Chambre enfant', 'Autre'],
      next: 'style',
      event: 'ROOM_SELECTED'
    },
    style: {
      prompt: 'Quelle ambiance souhaitez-vous créer ?',
      options: ['Hôtel contemporain', 'Japandi', 'Minéral', 'Nature', 'Floral', 'New York / Industriel', 'Artistique', 'Minimaliste', 'Élégant / intemporel'],
      next: 'colors',
      event: 'STYLE_SELECTED'
    },
    colors: {
      prompt: 'Quelles tonalités préférez-vous ?',
      options: ['Beige / crème', 'Blanc', 'Gris', 'Noir', 'Marron / terre', 'Vert', 'Bleu', 'Coloré', 'Peu importe'],
      next: 'intensity',
      event: 'COLOR_SELECTED'
    },
    intensity: {
      prompt: 'Quel résultat recherchez-vous ?',
      options: ['Très discret', 'Élégant mais présent', 'Mur signature / effet spectaculaire'],
      next: 'results',
      event: 'INTENSITY_SELECTED'
    }
  };

  function askQuestion(step) {
    var q = QUESTIONS[step];
    return {
      messages: [
        { type: 'bot_text', text: q.prompt },
        { type: 'bot_buttons', buttons: q.options.map(function (opt) { return { label: opt, action: 'answer_' + step, payload: opt }; }) }
      ]
    };
  }

  function handleButton(session, action, payload) {
    var Analytics = Lune.Analytics;

    if (action === 'find_decor') {
      Analytics.track(Analytics.EVENTS.START_RECOMMENDATION);
      session.flow = 'room';
      return askQuestion('room');
    }

    if (action.indexOf('answer_') === 0) {
      var step = action.replace('answer_', '');
      var q = QUESTIONS[step];
      if (!q) return { messages: [] };

      if (payload !== 'Peu importe' && payload !== 'Autre') {
        session.answers[step] = session.answers[step] || [];
        if (step === 'intensity') {
          session.answers.intensity = payload;
        } else if (session.answers[step].indexOf(payload) === -1) {
          session.answers[step].push(payload);
        }
      }
      Analytics.track(Analytics.EVENTS[q.event], { value: payload });

      if (q.next === 'results') {
        return showResults(session);
      }
      session.flow = q.next;
      return askQuestion(q.next);
    }

    if (action === 'choose_finish') {
      return showFinish(session);
    }

    if (action === 'ask_question') {
      return {
        messages: [
          { type: 'bot_text', text: 'Posez votre question — livraison, pose, matières, dimensions, finitions... Je fais de mon mieux pour vous répondre.' }
        ]
      };
    }

    if (action === 'resume_selection') {
      return showResults(session);
    }

    if (action === 'restart') {
      session.answers = { room: [], style: [], colors: [], intensity: null };
      session.flow = 'greeting';
      return {
        messages: [
          { type: 'bot_text', text: 'Reprenons. ' + QUESTIONS.room.prompt },
          { type: 'bot_buttons', buttons: QUESTIONS.room.options.map(function (opt) { return { label: opt, action: 'answer_room', payload: opt }; }) }
        ]
      };
    }

    // pwp_link / product_link / material_link : purs événements analytics,
    // la navigation réelle est un <a href> classique géré par l'UI.
    if (action === 'pwp_link') {
      Analytics.track(Analytics.EVENTS.PWP_CLICKED, payload || {});
      return { messages: [] };
    }
    if (action === 'product_link') {
      Analytics.track(Analytics.EVENTS.PRODUCT_CLICKED, payload || {});
      return { messages: [] };
    }
    if (action === 'material_link') {
      Analytics.track(Analytics.EVENTS.MATERIAL_CLICKED, payload || {});
      return { messages: [] };
    }

    return { messages: [] };
  }

  function showResults(session) {
    var Analytics = Lune.Analytics;
    var outcome = Lune.Recommend.recommend(session.catalog, session.answers, 3);

    try {
      window.sessionStorage.setItem('lune_selection', JSON.stringify({
        answers: session.answers,
        resultIds: outcome.results.map(function (r) { return r.id; })
      }));
    } catch (e) { /* stockage indisponible : on continue sans mémoriser */ }

    Analytics.track(Analytics.EVENTS.RESULTS_DISPLAYED, { count: outcome.results.length, hasStrongMatches: outcome.hasStrongMatches });

    if (!outcome.results.length) {
      return {
        messages: [
          { type: 'bot_text', text: 'Notre sélection s\'agrandit régulièrement et je n\'ai pas encore de décor à vous proposer précisément pour ces critères. N\'hésitez pas à parcourir nos collections ou à me poser une autre question.' }
        ]
      };
    }

    var intro = outcome.hasStrongMatches
      ? 'Voici les décors qui correspondent le mieux à votre projet :'
      : 'Notre sélection s\'agrandit régulièrement. Voici ce qui s\'en rapproche le plus pour l\'instant :';

    var cards = outcome.results.map(function (p) {
      return {
        image: p.image,
        title: p.title,
        reason: p.reason,
        primaryCta: { label: 'Découvrir', url: p.url, action: 'product_link', payload: { id: p.id } },
        secondaryCta: { label: 'Configurer mon mur', url: p.url + '#lm-dimensions', action: 'pwp_link', payload: { id: p.id } }
      };
    });

    return { messages: [{ type: 'bot_text', text: intro }, { type: 'bot_cards', cards: cards }] };
  }

  function showFinish(session) {
    var Analytics = Lune.Analytics;
    var product = session.pageContext && session.pageContext.product;

    var intro = { type: 'bot_text', text: 'LUNEMUR propose trois niveaux de finition, applicables à chacun de nos décors :' };

    var tiers = [
      {
        key: 'essential',
        title: 'Essentiel',
        subtitle: 'Solution accessible pour les projets décoratifs classiques.',
        url: product ? product.essentialUrl : null
      },
      {
        key: 'premium',
        title: 'Premium',
        subtitle: 'Meilleur équilibre entre rendu, toucher, résistance et qualité perçue.',
        badge: '★ Recommandé',
        note: 'Le choix privilégié pour la majorité des projets LUNEMUR.',
        url: product ? product.premiumUrl : null
      },
      {
        key: 'signature',
        title: 'Signature',
        subtitle: 'Pour les projets recherchant un rendu ou une matière particulièrement exclusive.',
        url: product ? product.signatureUrl : null
      }
    ];

    Analytics.track(Analytics.EVENTS.PREMIUM_RECOMMENDED, { has_product_context: !!product });

    var cards = tiers.map(function (t) {
      return {
        eyebrow: t.badge || null,
        title: t.title,
        reason: t.subtitle + (t.note ? ' ' + t.note : ''),
        primaryCta: t.url ? { label: 'Voir cette finition', url: t.url, action: 'material_link', payload: { tier: t.key } } : null
      };
    });

    var messages = [intro, { type: 'bot_cards', cards: cards }];
    if (!product) {
      messages.push({
        type: 'bot_text',
        text: 'Choisissez d\'abord un décor pour que je puisse vous montrer ses trois finitions et leur prix réel.'
      });
      messages.push(greetingButtons());
    }
    return { messages: messages };
  }

  return { active: localProvider, localProvider: localProvider };
})();

Lune.Conversation = (function () {
  'use strict';

  function createSession(config) {
    var session = {
      knowledge: config.knowledge,
      catalog: config.catalog || [],
      pageContext: config.pageContext || {},
      answers: { room: [], style: [], colors: [], intensity: null },
      flow: 'greeting',
      listeners: []
    };

    Lune.currentKnowledgeFallback = config.knowledge && config.knowledge.fallback;

    session.subscribe = function (fn) { session.listeners.push(fn); };

    session.emit = function (result) {
      session.listeners.forEach(function (fn) { fn(result.messages || []); });
    };

    session.start = function () {
      Lune.Analytics.track(Lune.Analytics.EVENTS.OPEN);
      var restored = null;
      try {
        var raw = window.sessionStorage.getItem('lune_selection');
        if (raw) restored = JSON.parse(raw);
      } catch (e) { /* pas de mémoire disponible */ }

      var greetingMessages = [
        { type: 'bot_text', text: (config.copy && config.copy.greeting) || 'Bonjour, je suis LUNE ✦\nJe peux vous aider à trouver le décor idéal, choisir votre finition ou répondre à vos questions.' }
      ];
      var buttons = [
        { label: 'Trouver mon décor', action: 'find_decor' },
        { label: 'Choisir ma finition', action: 'choose_finish' },
        { label: 'J\'ai une question', action: 'ask_question' }
      ];
      if (restored && restored.resultIds && restored.resultIds.length) {
        buttons.unshift({ label: 'Reprendre ma sélection', action: 'resume_selection' });
      }
      greetingMessages.push({ type: 'bot_buttons', buttons: buttons });
      session.emit({ messages: greetingMessages });
    };

    session.sendText = function (text) {
      session.emit({ messages: [{ type: 'user_text', text: text }] });
      var result = Lune.Providers.active.respond(session, { type: 'text', text: text });
      session.emit(result);
    };

    session.sendButton = function (action, payload, label) {
      if (label) {
        session.emit({ messages: [{ type: 'user_text', text: label }] });
      }
      var result = Lune.Providers.active.respond(session, { type: 'button', action: action, payload: payload });
      session.emit(result);
    };

    return session;
  }

  return { createSession: createSession };
})();
